// @flow
/* eslint jsx-a11y/label-has-for:0 */
/* eslint jsx-a11y/no-static-element-interactions:0 */

import React, { Component } from 'react';
import * as Cookie from 'app/packages/cookie-utils';
import downloadFile from 'app/common/lib/downloadFile';
import ErrorModal from 'app/common/react/components/settings/UserSettingsPage/ErrorModal';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import resourceFactory from 'app/common/lib/ResourceFactory';
import Testable from 'app/common/react/ui/Testable';
import withResource from 'app/common/lib/react/withResource';
import { track } from 'app/common/lib/react/analytics';
import { Box, Button, RadioButton, Spinner, Text, TextArea } from 'gestalt';

type Props = {
  attemptToClaimWebsite: () => void,
  domainVerifyResource: {
    data: {
      verification_code: string,
    },
    error?: {
      message: string,
    },
    isLoaded: boolean,
  },
  onHide: () => void,
  onSuccess?: () => void,
  settingsUrl: string,
  website: string,
};

type State = {
  downloadClicked: boolean,
  modalStep: 1 | 2,
  verificationType: 'email' | 'filename' | 'metatag' | '',
};

export class WebsiteModal extends Component<Props, State> {
  state: State = {
    downloadClicked: false,
    modalStep: 1,
    verificationType: '',
  };

  handleForward = () => this.setState({ modalStep: 2 });

  handleBack = () => this.setState({ modalStep: 1 });

  handleSubmit = () => {
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.SETTINGS_CLAIM_WEBSITE_SUBMIT,
    });
    const submitQueue = resourceFactory.create('DomainVerifyResource', {
      type: this.state.verificationType,
    });
    submitQueue.callUpdate();
    this.props.onHide();
    this.props.attemptToClaimWebsite();
    Cookie.set(PCONST.EVENT_CLAIM_WEBSITE, '1', 24);
    if (this.props.onSuccess) {
      this.props.onSuccess();
    }
  };

  handleDownload = () => {
    this.setState({ downloadClicked: true });
    downloadFile('/website/confirm/download/');
  };

  /* Only way to do this without flash cross-browser */
  handleCopy = () => {
    const input = window.document.getElementById('textAreaMetatag');
    input.select();
    document.execCommand('copy');
  };

  renderNextFooter = (readyToSubmit: boolean) => (
    <Box alignItems="center" display="flex" justifyContent="end" paddingX={2}>
      <Testable id="claim-website-next">
        <Button
          color={readyToSubmit ? 'red' : 'gray'}
          text={i18n._('Next', 'Button label for completing step 1 of the claim website sequence')}
          onClick={this.handleForward}
          disabled={!readyToSubmit}
          inline
        />
      </Testable>
    </Box>
  );

  renderModalStep1 = () => {
    const { downloadClicked, verificationType } = this.state;
    const { verification_code: verificationCode } = this.props.domainVerifyResource.data;
    const readyToSubmit =
      verificationType === 'metatag' || (verificationType === 'filename' && downloadClicked);

    const metatag = '<meta name="p:domain_verify" content="' + verificationCode + '"/>';

    return (
      <Modal
        accessibilityCloseLabel={i18n._('Close modal', 'Label to close modal')}
        accessibilityModalLabel={i18n._(
          'Confirm your website',
          'Accessibility label for confirm website modal',
        )}
        footer={this.renderNextFooter(readyToSubmit)}
        heading={i18n._('Pick claim option', 'Modal heading for confirm website modal')}
        onDismiss={this.props.onHide}
        size="md"
      >
        <Box paddingY={7} paddingX={3}>
          <Box alignItems="start" display="flex" justifyContent="start">
            <Box paddingX={3} flex="shrink">
              <RadioButton
                checked={verificationType === 'metatag'}
                id="metatag"
                name="websiteVerification"
                onChange={() => this.setState({ verificationType: 'metatag' })}
                size="md"
                value="metatag"
              />
            </Box>
            <Box flex="grow" marginTop={1}>
              <Box marginBottom={1}>
                <Text color="darkGray" weight="bold">
                  {i18n._('Add HTML tag', 'Label for adding an HTML tag to claim a website')}
                </Text>
              </Box>
              <Box>
                <Text>
                  {i18n._(
                    'Paste this tag into the <head> section of your site’s index.html file',
                    'Label for adding an HTML tag to claim a website',
                  )}
                </Text>
              </Box>
            </Box>
          </Box>
          {verificationType === 'metatag' && (
            <Box
              alignItems="center"
              display="flex"
              justifyContent="between"
              paddingX={3}
              paddingY={3}
            >
              <Box column={12}>
                <TextArea
                  id="textAreaMetatag"
                  onChange={(event, value) => {
                    this.forceUpdate();
                  }}
                  value={metatag}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box marginBottom={8} paddingX={3}>
          <Box alignItems="start" display="flex" justifyContent="start">
            <Box paddingX={3} flex="shrink">
              <RadioButton
                checked={verificationType === 'filename'}
                id="filename"
                name="websiteVerification"
                size="md"
                onChange={() => this.setState({ verificationType: 'filename' })}
                value="filename"
              />
            </Box>
            <Box flex="grow" marginTop={1}>
              <Box marginBottom={1}>
                <Text color="darkGray" weight="bold">
                  {i18n._(
                    'Upload HTML file',
                    'Label for uploading an html file to claim a website',
                  )}
                </Text>
              </Box>
              <Box>
                <Text>
                  {i18n._(
                    "Download this file and upload it to your website's root directory",
                    'Label for uploading an html file to claim a website',
                  )}
                </Text>
              </Box>
            </Box>
          </Box>
          {verificationType === 'filename' && (
            <Box
              alignItems="center"
              display="flex"
              justifyContent="center"
              paddingX={3}
              paddingY={3}
            >
              <Button
                color={downloadClicked ? 'gray' : 'red'}
                onClick={this.handleDownload}
                text={i18n._('Download', 'Button label for downloading a file to claim a website')}
              />
            </Box>
          )}
        </Box>
      </Modal>
    );
  };

  renderModalStep2 = () => {
    const footer = (
      <Box alignItems="center" column={12} display="flex" justifyContent="end" paddingX={2}>
        <Box flex="shrink" marginRight={4}>
          <Button
            color="gray"
            text={i18n._(
              'Back',
              'Button label for going back to step 1 of the claim website sequence',
            )}
            onClick={this.handleBack}
          />
        </Box>
        <Box flex="shrink" marginLeft={4}>
          <Testable id="claim-website-submit">
            <Button
              color="red"
              text={i18n._(
                'Submit',
                'Button label for completing step 1 of the claim website sequence',
              )}
              onClick={this.handleSubmit}
            />
          </Testable>
        </Box>
      </Box>
    );

    return (
      <Modal
        accessibilityCloseLabel={i18n._('Close modal', 'Label to close modal')}
        accessibilityModalLabel={i18n._(
          'Submit for review',
          'Accessibility label for confirm website modal',
        )}
        footer={footer}
        heading={i18n._('Submit for review', 'Modal heading for confirm website modal')}
        onDismiss={this.props.onHide}
        size="md"
      >
        <Box display="flex" alignItems="center" justifyContent="center" paddingY={6} paddingX={6}>
          <Text>
            {i18n._(
              "All set? We'll check your site for the meta tag and email you in the next 24 hours.",
              'Confirmation message before claiming website',
            )}
          </Text>
        </Box>
      </Modal>
    );
  };

  render() {
    if (!this.props.domainVerifyResource.isLoaded) {
      const footer = (
        <Box alignItems="center" display="flex" justifyContent="end" paddingX={2}>
          <Button
            color="gray"
            text={i18n._(
              'Next',
              'Button label for completing step 1 of the claim website sequence',
            )}
            disabled
            inline
          />
        </Box>
      );

      return (
        <Modal
          accessibilityCloseLabel={i18n._('Close modal', 'Label to close modal')}
          accessibilityModalLabel={i18n._(
            'Claim your website',
            'Accessibility label for confirm website modal',
          )}
          footer={footer}
          heading={i18n._('Pick claim option', 'Modal heading for confirm website modal')}
          onDismiss={this.props.onHide}
          size="md"
        >
          <Box alignItems="center" display="flex" justifyContent="center" paddingY={10}>
            <Spinner
              accessibilityLabel={i18n._('Loading', 'Accessibility label for loading spinner')}
              show
            />
          </Box>
        </Modal>
      );
    } else if (this.props.domainVerifyResource.error) {
      return (
        <ErrorModal error={this.props.domainVerifyResource.error} onHide={this.props.onHide} />
      );
    }
    if (this.state.modalStep === 1) {
      return this.renderModalStep1();
    } else {
      return this.renderModalStep2();
    }
  }
}

export default withResource(
  {
    name: 'DomainVerifyResource',
    key: 'domainVerifyResource',
  },
  { allowStale: false },
)(WebsiteModal);
